import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import {
  SocioEconomicFields,
  emptySocioEconomic,
  isSocioEconomicComplete,
  socioEconomicFromProfile
} from './SocioEconomicFields';
import { SocioEconomicData } from '../../types/domain';
import {
  getFinancingTypeLabel,
  getParentEducationLabel
} from '../../lib/domain-data';

/**
 * FR-03 — the patient's own view of the socio-economic data (SoW Bagian 2.3).
 *
 * The SoW calls this "data tetap", filled once during onboarding, but also
 * says it can be edited if needed; this section is the "if needed" half. It
 * was its own /family-data route until FR-02's profile editor existed, and now
 * lives on /profile beside the FR-02 fields.
 *
 * It keeps its own Simpan rather than sharing the profile form's: every FR-02
 * field is required and none of these are, so one button would stop a parent
 * updating a phone number until they had also found the diagnosis date.
 */
export default function FamilyDataSection() {
  const { profile, user, refreshProfile } = useAuth();

  // Keyed on the four stored values, not the profile object. This section
  // shares a page with the FR-02 form, whose save refreshes the profile — a
  // dependency on the object would wipe a half-edited family form whenever
  // the other section was saved.
  const stored = useMemo(
    () => socioEconomicFromProfile(profile),
    [
      profile?.parent_education,
      profile?.family_income,
      profile?.financing_type,
      profile?.parent_contact
    ]
  );
  const [draft, setDraft] = useState<SocioEconomicData>(emptySocioEconomic);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The profile arrives asynchronously and is refreshed after a save, so the
  // draft follows it rather than being seeded once from an initial value that
  // was still null on first render.
  useEffect(() => {
    setDraft(stored);
  }, [stored]);

  const handleSave = async () => {
    if (!user) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await dataService.updateSocioEconomic(user.id, draft);
      await refreshProfile();
      setNotice('Data keluarga tersimpan.');
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan data keluarga.');
    } finally {
      setBusy(false);
    }
  };

  // Income is hidden from the patient's own profile by client request; it is
  // still asked at onboarding and shown to the doctor.
  const complete = isSocioEconomicComplete(stored, { ignoreIncome: true });

  return (
    <section className="screen-card" aria-labelledby="family-title" id="data-keluarga">
      <div id="family-title" className="section-title">
        Data keluarga
      </div>
      <div className="subtitle">
        Data ini cukup diisi sekali. Ubah kapan saja kalau ada yang berubah.
      </div>

      {complete ? (
        <dl className="family-summary">
          <div className="family-summary-row">
            <dt>Pendidikan orang tua</dt>
            <dd>{getParentEducationLabel(stored.parentEducation)}</dd>
          </div>
          <div className="family-summary-row">
            <dt>Jenis pembiayaan</dt>
            <dd>{getFinancingTypeLabel(stored.financingType)}</dd>
          </div>
          <div className="family-summary-row">
            <dt>Nomor kontak orang tua</dt>
            <dd>{stored.parentContact}</dd>
          </div>
        </dl>
      ) : (
        <div className="subtitle" style={{ marginTop: 12 }}>
          Data keluarga belum lengkap. Isi di bawah ini ya.
        </div>
      )}

      <div className="clinical-form">
        <div className="subtitle">Ubah data</div>

        <SocioEconomicFields
          value={draft}
          onChange={setDraft}
          disabled={busy}
          idPrefix="family"
          hideIncome
        />

        <div className="subtitle" style={{ fontSize: 11 }}>
          Boleh dikosongkan dulu dan dilengkapi nanti.
        </div>

        {error ? <div className="target-band-error">{error}</div> : null}
        {notice ? <div className="target-band-preview">{notice}</div> : null}

        <div className="target-actions">
          <button type="button" className="btn-primary" disabled={busy} onClick={handleSave}>
            {busy ? 'Menyimpan...' : 'Simpan data keluarga'}
          </button>
        </div>
      </div>
    </section>
  );
}
